import { getUsersByAccessCode } from 'api/users'
import NotificationSys from 'components/NotificationSystem'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import type { TeamMember, UserTeamMember } from 'types'
import { EOp, FilterModel } from 'types'

/**
 * Members are searched across the whole organization, so we wait for a couple of
 * characters instead of loading every trainee of the organization at once.
 */
export const MIN_SEARCH_LENGTH = 2

export interface MemberOption {
  label: string
  value: number
}

// Helper: Create label for member
export const createMemberLabel = (member: UserTeamMember): string => {
  return `${member.firstname} ${member.lastname} (${member.email})`
}

// Helper: Create option from member
export const createMemberOption = (member: UserTeamMember): MemberOption => {
  return {
    label: createMemberLabel(member),
    value: member.id,
  }
}

// Helper: Filter out members already in team
export const filterAvailableMembers = (
  members: UserTeamMember[],
  teamMembers: TeamMember[],
): UserTeamMember[] => {
  return members.filter((member) => {
    const hasAlreadyMemberInTeam = teamMembers.find((teamMember) => teamMember.userId === member.id)
    return !hasAlreadyMemberInTeam
  })
}

interface UseTeamMemberSearchParams {
  teamMembersData: TeamMember[]
  organizationId: number | undefined
  selectedMemberId?: number | null
}

interface UseTeamMemberSearchReturn {
  teamMembersOptions: MemberOption[]
  isLoading: boolean
  debouncedSearch: (value: string) => void
  selectedMember: UserTeamMember | null
  setSelectedMember: (member: UserTeamMember | null) => void
  membersData: UserTeamMember[]
  hasMinSearchLength: boolean
}

export const useTeamMemberSearch = ({
  teamMembersData,
  selectedMemberId,
  organizationId,
}: UseTeamMemberSearchParams): UseTeamMemberSearchReturn => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedMember, setSelectedMember] = useState<UserTeamMember | null>(null)

  const trimmedSearch = searchTerm.trim()
  const hasMinSearchLength = trimmedSearch.length >= MIN_SEARCH_LENGTH

  const membersQuery = useQuery(
    ['membersList', trimmedSearch, organizationId],
    () => {
      // Search by firstname, lastname or email across the entire organization:
      // a member of any Access Code can be added to the team.
      let filter: FilterModel = {
        or: [
          {
            firstname: {
              type: 'text',
              op: EOp.contains,
              value: trimmedSearch,
            },
          },
          {
            lastname: {
              type: 'text',
              op: EOp.contains,
              value: trimmedSearch,
            },
          },
          {
            email: {
              type: 'text',
              op: EOp.contains,
              value: trimmedSearch,
            },
          },
        ],
      }

      // Add organizationId filter if provided
      if (organizationId !== undefined) {
        filter = {
          ...filter,
          organizationId: {
            type: 'text',
            op: EOp.equals,
            value: String(organizationId),
          },
        }
      }

      return getUsersByAccessCode({ filter })
    },
    {
      enabled: hasMinSearchLength,
      keepPreviousData: true,
      staleTime: 30000,
      onError: (error) => {
        NotificationSys.showWarning('Failed to load members')
        console.error('Members query error:', error)
      },
    },
  )

  // Until the search is long enough there is nothing to offer, whatever is left in the cache.
  const membersData = useMemo(
    () => (hasMinSearchLength ? membersQuery.data?.data || [] : []),
    [hasMinSearchLength, membersQuery.data?.data],
  )

  const teamMembersOptions = useMemo(() => {
    const availableMembers = filterAvailableMembers(membersData, teamMembersData)
    const filteredOptions = availableMembers.map(createMemberOption)

    // If there is a selected member that is not in the current filtered list, add it
    if (selectedMember && !filteredOptions.find((opt) => opt.value === selectedMember.id)) {
      return [createMemberOption(selectedMember), ...filteredOptions]
    }

    return filteredOptions
  }, [membersData, teamMembersData, selectedMember])

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const debouncedSearch = useMemo(() => debounce(handleSearchChange, 400), [handleSearchChange])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  // Sync selectedMember with selectedMemberId from parent
  useEffect(() => {
    if (selectedMemberId) {
      const member = membersData.find((item) => item.id === selectedMemberId)
      if (member) {
        setSelectedMember(member)
      }
    } else {
      setSelectedMember(null)
    }
  }, [selectedMemberId, membersData])

  return {
    teamMembersOptions,
    isLoading: hasMinSearchLength && (membersQuery.isLoading || membersQuery.isFetching),
    debouncedSearch,
    selectedMember,
    setSelectedMember,
    membersData,
    hasMinSearchLength,
  }
}
